/**
 * MIS ITEMS COMO LISTA COMPARATIVA (MADERINCO-MIS-ITEMS-REDISENO-T1, parte 1; diseno de Atenea #31063, alternativa A).
 *
 * Una fila por producto (identidad = su id; nunca se juntan dos por tener el mismo nombre): nombre completo, unidad,
 * precio y estado. Un precio que no viene es "Por definir", distinto de un precio en cero. Lo que da la API hoy
 * (PresupuestoProductoDto): Id, Nombre, Descripcion, Unidad, PrecioVenta, PrecioMayor, MayorDesdeUnidades, Activo.
 * Parte 2 (nexo-api 579): con GET productos?conUbicaciones=true cada producto trae ubicaciones [{ seccionId, seccion,
 * paso }]; lista vacia = no esta ubicado; sin el campo (API vieja) = null y los filtros de seccion y paso no se muestran.
 *
 * @module application/presupuestos/misItems
 */
import { PASOS } from './ubicacionesProducto.js';

const ETIQUETA_PASO = Object.fromEntries(PASOS.map((p) => [p.id, p.etiqueta]));
/** El paso como lo lee la persona ('1. Tipo'...); uno desconocido se muestra tal cual. */
export const etiquetaPaso = (paso) => ETIQUETA_PASO[paso] || String(paso || '');

function leerUbicaciones(crudas) {
  if (!Array.isArray(crudas)) return null;
  return crudas.map((u) => ({
    seccionId: String(u.seccionId ?? u.SeccionId ?? ''),
    seccion: String(u.seccion ?? u.Seccion ?? ''),
    paso: String(u.paso ?? u.Paso ?? '')
  })).filter((u) => u.seccionId);
}

/** Producto de la API (camel, Pascal o snake) a fila. precio null = no vino. */
export function filaDeProducto(p) {
  const v = (a, b, c) => p?.[a] ?? p?.[b] ?? (c ? p?.[c] : undefined);
  const precio = v('precioVenta', 'PrecioVenta', 'precio_venta');
  const activo = v('activo', 'Activo');
  return {
    id: String(v('id', 'Id') ?? ''),
    nombre: String(v('nombre', 'Nombre') ?? '').trim() || '(sin nombre)',
    descripcion: String(v('descripcion', 'Descripcion') ?? ''),
    unidad: String(v('unidad', 'Unidad') ?? ''),
    precio: precio === null || precio === undefined || precio === '' ? null : Number(precio),
    precioMayor: Number(v('precioMayor', 'PrecioMayor', 'precio_mayor') ?? 0) || 0,
    mayorDesde: Number(v('mayorDesdeUnidades', 'MayorDesdeUnidades', 'mayor_desde_unidades') ?? 0) || 0,
    activo: activo === undefined || activo === null ? true : !!activo,
    ubicaciones: leerUbicaciones(v('ubicaciones', 'Ubicaciones'))
  };
}

/** La API ya manda las ubicaciones (579 servido): solo entonces se ofrecen los filtros de seccion y paso. */
export function hayUbicaciones(filas) {
  return filas.some((f) => f.ubicaciones !== null);
}

/** Opciones de los filtros: secciones (por nombre) y pasos (en el orden 1, 2, 3) que de verdad aparecen. */
export function opcionesDeFiltro(filas) {
  const secciones = new Map();
  const pasos = new Set();
  for (const f of filas) for (const u of f.ubicaciones || []) { secciones.set(u.seccionId, u.seccion || 'Sección'); pasos.add(u.paso); }
  const orden = PASOS.map((p) => p.id);
  return {
    secciones: [...secciones].map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    pasos: [...pasos].sort((a, b) => (orden.indexOf(a) + 1 || 99) - (orden.indexOf(b) + 1 || 99)).map((id) => ({ id, etiqueta: etiquetaPaso(id) })),
    sinUbicar: filas.some((f) => Array.isArray(f.ubicaciones) && f.ubicaciones.length === 0)
  };
}

/** Estado que ve la persona. Solo lo que la API sabe hoy: activo o inactivo. */
export function estadoDeFila(f) {
  return f.activo ? 'Disponible' : 'Inactivo';
}

/**
 * Filtra por estado ('todos' | 'activos' | 'inactivos'), seccion (id, '' = todas, 'sin_ubicar') y paso (id, '' = todos).
 * Seccion y paso juntos piden que la MISMA ubicacion cumpla los dos (el paso es propio de cada seccion). No toca el
 * orden ni junta filas.
 */
export function filtrarFilas(filas, { estado = 'todos', seccion = '', paso = '' } = {}) {
  return filas.filter((f) => {
    if (estado === 'activos' && !f.activo) return false;
    if (estado === 'inactivos' && f.activo) return false;
    if (seccion === 'sin_ubicar') return Array.isArray(f.ubicaciones) && f.ubicaciones.length === 0;
    if (!seccion && !paso) return true;
    return (f.ubicaciones || []).some((u) => (!seccion || u.seccionId === seccion) && (!paso || u.paso === paso));
  });
}

/** "N resultados" del conjunto consultado (no de lo que cabe en pantalla). */
export function textoResultados(n, filtrado) {
  return `${n} ${n === 1 ? 'resultado' : 'resultados'}${filtrado ? ' con este filtro' : ''}`;
}

/**
 * claridad v0.5 (catalogos.md): una referencia de plantilla sin producto no es un producto disponible; se cuenta
 * APARTE y no se suma a los productos. Lo sabe solo una plantilla que ya trae productoId (nexo-api 577); con una API
 * vieja (todos undefined) no se sabe y no se dice nada.
 */
export function referenciasSinProducto(plantilla) {
  const items = (plantilla?.secciones || []).flatMap((s) => s.items || []);
  const conocido = items.some((it) => it.productoId !== undefined);
  return { conocido, sinProducto: conocido ? items.filter((it) => it.productoId === null).length : 0, total: items.length };
}
